import { type LoadInput, type LoadEntry, type ChargerInput, type LoadUnit } from '@brt/shared-types'
import type { AppStore } from '../useAppStore.ts'

export interface LoadSlice {
  load: LoadInput
  charger: ChargerInput
  addLoad: (load?: Partial<LoadEntry>) => void
  updateLoad: (loadId: string, updates: Partial<LoadEntry>) => void
  removeLoad: (loadId: string) => void
  toggleLoad: (loadId: string) => void
  setDutyCycle: (dutyCycle: LoadInput['dutyCycle']) => void
  setCharger: (charger: Partial<ChargerInput>) => void
}

function generateId(): string {
  return `load-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
}

const DEFAULT_LOAD: LoadInput = {
  loads: [{
    id: 'load-default',
    name: 'Load 1',
    loadValue: 5,
    loadUnit: 'A' as LoadUnit,
    enabled: true,
    isPreset: false,
  }],
}

const DEFAULT_CHARGER: ChargerInput = {
  chargerCurrentValue: 10,
  chargerVoltage: 12,
  chargerPower: 0,
  chargeEfficiency: 0.90,
}

type Set = (fn: (state: AppStore) => Partial<AppStore>) => void
type Get = () => AppStore

export function createLoadSlice(set: Set, _get: Get): LoadSlice {
  return {
    load: { ...DEFAULT_LOAD, loads: DEFAULT_LOAD.loads.map(l => ({ ...l })) },
    charger: { ...DEFAULT_CHARGER },

    addLoad: (overrides) =>
      set((state) => {
        const newEntry: LoadEntry = {
          id: generateId(),
          name: `Load ${state.load.loads.length + 1}`,
          loadValue: 0,
          loadUnit: 'A' as LoadUnit,
          enabled: true,
          isPreset: false,
          ...overrides,
        }
        return {
          load: {
            ...state.load,
            loads: [...state.load.loads, newEntry],
          },
        }
      }),

    updateLoad: (loadId, updates) =>
      set((state) => ({
        load: {
          ...state.load,
          loads: state.load.loads.map((entry) =>
            entry.id === loadId ? { ...entry, ...updates } : entry
          ),
        },
      })),

    removeLoad: (loadId) =>
      set((state) => {
        if (state.load.loads.length <= 1) return {}
        return {
          load: {
            ...state.load,
            loads: state.load.loads.filter((entry) => entry.id !== loadId),
          },
        }
      }),

    toggleLoad: (loadId) =>
      set((state) => ({
        load: {
          ...state.load,
          loads: state.load.loads.map((entry) =>
            entry.id === loadId ? { ...entry, enabled: !entry.enabled } : entry
          ),
        },
      })),

    setDutyCycle: (dutyCycle) =>
      set((state) => {
        const newLoad = { ...state.load }
        if (dutyCycle) {
          newLoad.dutyCycle = dutyCycle
        } else {
          delete newLoad.dutyCycle
        }
        return { load: newLoad }
      }),

    setCharger: (updates) =>
      set((state) => ({
        charger: { ...state.charger, ...updates },
      })),
  }
}

export { DEFAULT_LOAD, DEFAULT_CHARGER }
